import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  IconButton,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Tooltip,
  CircularProgress,
  Badge,
  Tab,
  Tabs,
  Collapse,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

// Icons
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PieChartIcon from '@mui/icons-material/PieChart';

// Firebase
import { auth, db } from './firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { 
  collection, 
  onSnapshot, 
  addDoc, 
  setDoc, 
  doc, 
  query, 
  orderBy,
  where,
  getDocs,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const AdminPanel = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // משתמשים מורשים
  const allowedEmails = ['meirl@darkcrypto.com', 'haital@darkcrypto.com'];
  const currentUser = auth.currentUser;
  const isAuthorized = currentUser && allowedEmails.includes(currentUser.email);

  // State
  const [trades, setTrades] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState(null);
  
  // סטטיסטיקות מתקדמות
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTrades: 0,
    averageR: 0,
    winRate: 0,
    tradesWithTrigger: 0,
    tradesWithoutTrigger: 0,
    triggerSuccessRate: 0,
    sessionStats: {},
    dayOfWeekStats: {},
    timeOfDayStats: {},
    bestPerformingUser: null,
    recentTradesTrend: 0,
  });

  // דיאלוג יצירת משתמש
  const [openCreateUserDialog, setOpenCreateUserDialog] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserTrigger, setNewUserTrigger] = useState('');
  const [newUserTradingHours, setNewUserTradingHours] = useState('');

  // דיאלוג משתמש ספציפי
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTrades, setUserTrades] = useState([]);
  const [userTabValue, setUserTabValue] = useState(0);

  // עמודות טבלה
  const columns = [
    { id: 'date', label: 'תאריך', align: 'right' },
    { id: 'dayOfWeek', label: 'יום בשבוע', align: 'right' },
    { id: 'time', label: 'שעה', align: 'right' },
    { id: 'session', label: 'סשן', align: 'right' },
    { id: 'triggerAppeared', label: 'טריגר הופיע', align: 'center' },
    { id: 'matchedTrigger', label: 'טריגר תואם', align: 'right' },
    { id: 'report', label: 'דוח', align: 'right' },
    { id: 'stopPercentage', label: 'סטופ (%)', align: 'right' },
    { id: 'outcome', label: 'תוצאה', align: 'center' },
    { id: 'tpSelected', label: 'TP נבחר', align: 'right' },
    { id: 'duration', label: 'משך (דקות)', align: 'right' },
  ];

  // פונקציית חישוב R
  function getTradeR(trade) {
    if (trade.outcome === 'Stop Loss') return -1;
    if (trade.outcome === 'Take Profit') {
      if (trade.tpMilestoneExit) {
        const parts = trade.tpMilestoneExit.split(':');
        if (parts.length === 2) {
          return parseFloat(parts[1]);
        }
      }
      return 1;
    }
    return 0;
  }

  // סגנונות
  const styles = {
    root: {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
      minHeight: '100vh',
    },
    appBar: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    },
    mainContent: {
      mt: 8,
      p: 3,
      direction: 'rtl',
    },
    statsCard: {
      borderRadius: 16,
      boxShadow: '0 8px 16px rgba(0,0,0,0.05)',
      transition: 'transform 0.3s, box-shadow 0.3s',
      height: '100%',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
      },
    },
    actionButton: {
      borderRadius: 8,
      boxShadow: 'none',
      fontWeight: 500,
      textTransform: 'none',
    },
    userListItem: {
      mb: 2,
      cursor: 'pointer',
      transition: 'all 0.2s',
      borderRadius: 2,
      border: '1px solid transparent',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        borderColor: alpha(theme.palette.primary.main, 0.2),
        transform: 'translateY(-2px)',
      }
    },
    tradeRow: (outcome) => ({
      backgroundColor: 
        outcome === 'Take Profit' 
          ? alpha(theme.palette.success.main, 0.07)
          : outcome === 'Stop Loss'
            ? alpha(theme.palette.error.main, 0.07)
            : 'inherit',
      transition: 'background-color 0.3s',
      '&:hover': {
        backgroundColor: 
          outcome === 'Take Profit' 
            ? alpha(theme.palette.success.main, 0.15)
            : outcome === 'Stop Loss'
              ? alpha(theme.palette.error.main, 0.15)
              : alpha(theme.palette.primary.main, 0.08),
      }
    }),
    outcomeChip: (outcome) => ({
      backgroundColor: 
        outcome === 'Take Profit' 
          ? theme.palette.success.main
          : outcome === 'Stop Loss'
            ? theme.palette.error.main
            : theme.palette.warning.main,
      color: '#fff',
      fontWeight: 'bold',
    }),
    searchInput: {
      borderRadius: 20,
      width: '100%',
      backgroundColor: alpha(theme.palette.common.white, 0.15),
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
      },
      '& .MuiInputBase-input': {
        padding: theme.spacing(1, 2, 1, 2),
      },
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 'bold',
      textAlign: 'center',
      color: theme.palette.primary.main,
    },
    gradientButton: {
      background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', 
      fontSize: '1.2rem', 
      padding: '14px 28px', 
      borderRadius: '12px', 
      fontWeight: 'bold',
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
      transition: '0.3s',
      '&:hover': { 
        background: 'linear-gradient(135deg, #5a0db1 0%, #1e63e9 100%)', 
        transform: 'scale(1.05)' 
      }
    },
    accordion: {
      backgroundColor: alpha(theme.palette.background.paper, 0.6),
      borderRadius: '12px',
      mb: 2,
      '&:before': {
        display: 'none',
      },
    },
    accordionSummary: {
      borderRadius: '12px',
      '&.Mui-expanded': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }
    }
  };

  // שליפת עסקאות
  useEffect(() => {
    setIsLoading(true);
    const tradesRef = collection(db, 'trades');
    const q = query(tradesRef, orderBy('date', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allTrades = [];
      snapshot.forEach((docSnap) => {
        allTrades.push({ id: docSnap.id, ...docSnap.data() });
      });
      setTrades(allTrades);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // חישוב סטטיסטיקות משתמשים
  useEffect(() => {
    if (!trades.length) return;

    const totalTrades = trades.length;
    const usersSet = new Set(trades.map((t) => t.uid));
    const totalUsers = usersSet.size;

    // חישוב סטטיסטיקות R וWin Rate
    let sumR = 0;
    let winCount = 0;
    let tradesWithTrigger = 0;
    let tradesWithoutTrigger = 0;
    let triggerSuccessCount = 0;
    let withoutTriggerWinCount = 0;
    let withoutTriggerSumR = 0;
    let withTriggerSumR = 0;
    
    // סטטיסטיקות נוספות
    const sessionStats = {};
    const dayOfWeekStats = {};
    const timeOfDayStats = {
      'בוקר (05:00-12:00)': { count: 0, wins: 0, r: 0 },
      'צהריים (12:00-17:00)': { count: 0, wins: 0, r: 0 },
      'ערב (17:00-21:00)': { count: 0, wins: 0, r: 0 },
      'לילה (21:00-05:00)': { count: 0, wins: 0, r: 0 },
    };
    
    // סטטיסטיקות של עסקאות ללא טריגר
    const noTriggerSessionStats = {};
    const noTriggerDayStats = {};

    trades.forEach((trade) => {
      // חישוב R וwin rate
      const r = getTradeR(trade);
      sumR += r;
      
      if (trade.outcome === 'Take Profit') {
        winCount++;
      }
      
      // ספירת וחישוב עסקאות עם/בלי טריגר
      if (trade.triggerAppeared === 'כן') {
        tradesWithTrigger++;
        withTriggerSumR += r;
        if (trade.outcome === 'Take Profit') {
          triggerSuccessCount++;
        }
      } else {
        tradesWithoutTrigger++;
        withoutTriggerSumR += r;
        if (trade.outcome === 'Take Profit') {
          withoutTriggerWinCount++;
        }
        
        // סטטיסטיקות נוספות לעסקאות ללא טריגר
        const session = trade.session || 'לא צוין';
        if (!noTriggerSessionStats[session]) {
          noTriggerSessionStats[session] = { count: 0, wins: 0, r: 0 };
        }
        noTriggerSessionStats[session].count++;
        noTriggerSessionStats[session].r += r;
        if (trade.outcome === 'Take Profit') {
          noTriggerSessionStats[session].wins++;
        }
        
        const dayOfWeek = trade.dayOfWeek || 'לא צוין';
        if (!noTriggerDayStats[dayOfWeek]) {
          noTriggerDayStats[dayOfWeek] = { count: 0, wins: 0, r: 0 };
        }
        noTriggerDayStats[dayOfWeek].count++;
        noTriggerDayStats[dayOfWeek].r += r;
        if (trade.outcome === 'Take Profit') {
          noTriggerDayStats[dayOfWeek].wins++;
        }
      }
      
      // סטטיסטיקות לפי סשן
      const session = trade.session || 'לא צוין';
      if (!sessionStats[session]) {
        sessionStats[session] = { count: 0, wins: 0, r: 0 };
      }
      sessionStats[session].count++;
      sessionStats[session].r += r;
      if (trade.outcome === 'Take Profit') {
        sessionStats[session].wins++;
      }
      
      // סטטיסטיקות לפי יום בשבוע
      const dayOfWeek = trade.dayOfWeek || 'לא צוין';
      if (!dayOfWeekStats[dayOfWeek]) {
        dayOfWeekStats[dayOfWeek] = { count: 0, wins: 0, r: 0 };
      }
      dayOfWeekStats[dayOfWeek].count++;
      dayOfWeekStats[dayOfWeek].r += r;
      if (trade.outcome === 'Take Profit') {
        dayOfWeekStats[dayOfWeek].wins++;
      }
      
      // סטטיסטיקות לפי שעה ביום
      const hour = trade.time ? parseInt(trade.time.split(':')[0]) : null;
      if (hour !== null) {
        let timeCategory;
        if (hour >= 5 && hour < 12) {
          timeCategory = 'בוקר (05:00-12:00)';
        } else if (hour >= 12 && hour < 17) {
          timeCategory = 'צהריים (12:00-17:00)';
        } else if (hour >= 17 && hour < 21) {
          timeCategory = 'ערב (17:00-21:00)';
        } else {
          timeCategory = 'לילה (21:00-05:00)';
        }
        
        timeOfDayStats[timeCategory].count++;
        timeOfDayStats[timeCategory].r += r;
        if (trade.outcome === 'Take Profit') {
          timeOfDayStats[timeCategory].wins++;
        }
      }
    });
    
    const averageR = totalTrades ? sumR / totalTrades : 0;
    const winRate = totalTrades ? (winCount / totalTrades) * 100 : 0;
    const triggerSuccessRate = tradesWithTrigger ? (triggerSuccessCount / tradesWithTrigger) * 100 : 0;
    const withoutTriggerWinRate = tradesWithoutTrigger ? (withoutTriggerWinCount / tradesWithoutTrigger) * 100 : 0;
    const withoutTriggerAverageR = tradesWithoutTrigger ? withoutTriggerSumR / tradesWithoutTrigger : 0;
    const withTriggerAverageR = tradesWithTrigger ? withTriggerSumR / tradesWithTrigger : 0;
    
    // מציאת הסשן הטוב ביותר ללא טריגר
    let bestNoTriggerSession = null;
    let bestNoTriggerSessionAvgR = -Infinity;
    
    for (const [session, data] of Object.entries(noTriggerSessionStats)) {
      if (data.count >= 3) { // מינימום 3 עסקאות לסשן
        const avgR = data.r / data.count;
        if (avgR > bestNoTriggerSessionAvgR) {
          bestNoTriggerSession = {
            name: session,
            count: data.count,
            avgR,
            winRate: (data.wins / data.count) * 100
          };
          bestNoTriggerSessionAvgR = avgR;
        }
      }
    }
    
    // מגמת 20 העסקאות האחרונות
    const lastTwentyTrades = trades.slice(0, 20);
    const lastTwentyR = lastTwentyTrades.reduce((acc, t) => acc + getTradeR(t), 0);
    const recentTradesTrend = lastTwentyR / 20;
    
    // נתוני משתמשים
    const userMap = {};
    trades.forEach((t) => {
      const uid = t.uid;
      const name = t.userName || 'לא ידוע';
      if (!userMap[uid]) userMap[uid] = { uid, name, tradesCount: 0, userR: 0, wins: 0, losses: 0 };
      userMap[uid].tradesCount += 1;
      userMap[uid].userR += getTradeR(t);
      if (t.outcome === 'Take Profit') {
        userMap[uid].wins += 1;
      } else if (t.outcome === 'Stop Loss') {
        userMap[uid].losses += 1;
      }
    });
    
    // מציאת המשתמש הטוב ביותר
    let bestPerformingUser = null;
    let bestR = -Infinity;
    
    const targetTrades = 400;
    const usersArray = Object.values(userMap).map((user) => {
      const winRate = user.tradesCount ? (user.wins / user.tradesCount) * 100 : 0;
      const averageR = user.tradesCount ? user.userR / user.tradesCount : 0;
      
      // בדיקה אם זה המשתמש הטוב ביותר
      if (user.tradesCount >= 10 && averageR > bestR) {
        bestR = averageR;
        bestPerformingUser = { ...user, winRate, averageR };
      }
      
      return {
        ...user,
        winRate,
        averageR,
        progress: Math.min(100, (user.tradesCount / targetTrades) * 100),
      };
    });
    
    setUsersData(usersArray);
    
    // עדכון כל הסטטיסטיקות
    setStats({
      totalUsers,
      totalTrades,
      averageR,
      winRate,
      tradesWithTrigger,
      tradesWithoutTrigger,
      triggerSuccessRate,
      withoutTriggerWinRate,
      withoutTriggerAverageR,
      withTriggerAverageR,
      sessionStats,
      dayOfWeekStats,
      timeOfDayStats,
      bestPerformingUser,
      recentTradesTrend,
      noTriggerSessionStats,
      noTriggerDayStats,
      bestNoTriggerSession,
    });
  }, [trades]);

  // פונקציות
  const handleCreateUser = async () => {
    if (!newUserName || !newUserPassword || !newUserTrigger || !newUserTradingHours) {
      alert('נא להזין את כל הפרטים: שם משתמש, סיסמה, טריגר ושעות מסחר');
      return;
    }
    
    try {
      setIsLoading(true);
      const email = `${newUserName}@darkcrypto.com`.toLowerCase();
      const userCredential = await createUserWithEmailAndPassword(auth, email, newUserPassword);
      const newUser = userCredential.user;
      
      await setDoc(doc(db, 'users', newUser.uid), {
        trigger: newUserTrigger,
        tradingHours: newUserTradingHours,
        userName: newUserName,
        email,
        createdAt: new Date().toISOString(),
      });
      
      alert('משתמש חדש נוצר בהצלחה!');
      setOpenCreateUserDialog(false);
      resetUserForm();
    } catch (error) {
      console.error('שגיאה ביצירת משתמש:', error);
      alert(`אירעה שגיאה ביצירת המשתמש: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const resetUserForm = () => {
    setNewUserName('');
    setNewUserPassword('');
    setNewUserTrigger('');
    setNewUserTradingHours('');
  };

  const handleLogout = () => {
    auth.signOut().then(() => {
      navigate('/login');
    }).catch((error) => {
      console.error('שגיאה בהתנתקות:', error);
    });
  };

  // טיפול בלחיצה על משתמש
  const handleUserClick = (user) => {
    setSelectedUser(user);
    const filteredTrades = trades.filter((t) => t.uid === user.uid);
    setUserTrades(filteredTrades);
    setUserTabValue(0);
  };

  const handleCloseUserDialog = () => {
    setSelectedUser(null);
    setUserTrades([]);
  };
  
  // פונקציית עזר להצגת ערך התקדמות באחוזים
  const getProgressColorAndIcon = (value) => {
    if (value > 0.5) return { color: 'success', icon: <TrendingUpIcon /> };
    if (value >= 0) return { color: 'warning', icon: <TrendingUpIcon /> };
    return { color: 'error', icon: <TrendingDownIcon /> };
  };
  
  // פונקציה לסינון משתמשים לפי חיפוש
  const getFilteredUsers = () => {
    if (!searchQuery) return usersData;
    
    return usersData.filter(user => 
      user.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };
  
  // פונקציה לעיגול מספר לדיוק מסוים
// Update this function in your code
const roundNumber = (num, precision = 2) => {
  // Check if num is undefined, null, or not a number
  if (num === undefined || num === null || isNaN(num)) {
    return 0; // Return a default value (0) instead of trying to use toFixed
  }
  return parseFloat(num.toFixed(precision));
};
  // פונקציה להצגת תמונת תוצאת העסקה
  const renderOutcomeIcon = (outcome) => {
    if (outcome === 'Take Profit') {
      return (
        <Tooltip title="Take Profit">
          <CheckCircleIcon color="success" fontSize="small" />
        </Tooltip>
      );
    } else if (outcome === 'Stop Loss') {
      return (
        <Tooltip title="Stop Loss">
          <CancelIcon color="error" fontSize="small" />
        </Tooltip>
      );
    }
    return null;
  };
  
  // פונקציה להצגת אייקון של כן/לא
  const renderYesNoIcon = (value) => {
    if (value === 'כן') {
      return <CheckBoxIcon color="success" fontSize="small" />;
    } else {
      return <CloseIcon color="error" fontSize="small" />;
    }
  };

  // אם המשתמש לא מורשה - אין גישה
  if (!isAuthorized) return null;

  // מסך טעינה
  if (isLoading && !trades.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      {/* Header */}
      <AppBar position="fixed" color="inherit" sx={styles.appBar}>
        <Toolbar>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            {/* לוגו וכותרת */}
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                <AdminPanelSettingsIcon />
              </Avatar>
              <Typography variant="h6" fontWeight="bold" noWrap>
                פאנל מנהלים - מאיץ הרווחים
              </Typography>
            </Box>
            
            {/* כפתורים */}
            <Box display="flex" alignItems="center" gap={1}>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<ExitToAppIcon />}
                sx={styles.actionButton}
                onClick={() => navigate('/dashboard')}
              >
                חזרה לדאשבורד
              </Button>
              <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={<ExitToAppIcon />}
                sx={styles.actionButton}
                onClick={handleLogout}
              >
                התנתק
              </Button>
              <Tooltip title={currentUser?.email || 'משתמש'}>
                <Avatar
                  sx={{ 
                    ml: 1, 
                    bgcolor: theme.palette.secondary.main,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 0 0 2px rgba(0,0,0,0.1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  <AccountCircleIcon />
                </Avatar>
              </Tooltip>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* תוכן עיקרי */}
      <Box component="main" sx={styles.mainContent}>
        {/* כפתורי פעולה */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              sx={styles.gradientButton}
              onClick={() => setOpenCreateUserDialog(true)}
            >
              יצירת משתמש חדש
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button 
              fullWidth
              variant="contained"
              startIcon={<EqualizerIcon />}
              sx={styles.gradientButton}
              onClick={() => window.location.href = '/UTSP'}
            >
              פירוט כל הסטטיסטיקות
            </Button>
          </Grid>
        </Grid>

        {/* כרטיסי סטטיסטיקה עיקריים */}
        <Accordion sx={styles.accordion} expanded={expandedSection === 'mainStats'} onChange={() => setExpandedSection(expandedSection === 'mainStats' ? null : 'mainStats')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
            <Box display="flex" alignItems="center">
              <AssessmentIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">סטטיסטיקות מרכזיות</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card sx={styles.statsCard}>
                  <CardContent>
                    <Typography variant="subtitle1" align="center" gutterBottom>
                      סה"כ עסקאות
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                      <ShowChartIcon color="primary" fontSize="large" />
                    </Box>
                    <Typography sx={styles.statValue}>
                      {stats.totalTrades}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={6} md={3}>
                <Card sx={styles.statsCard}>
                  <CardContent>
                    <Typography variant="subtitle1" align="center" gutterBottom>
                      WIN RATE עם טריגר
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                      <PieChartIcon color="primary" fontSize="large" />
                    </Box>
                    <Typography sx={styles.statValue} color={stats.triggerSuccessRate > 60 ? 'success.main' : 'primary.main'}>
                      {roundNumber(stats.triggerSuccessRate)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={6} md={3}>
                <Card sx={styles.statsCard}>
                  <CardContent>
                    <Typography variant="subtitle1" align="center" gutterBottom>
                      ממוצע R עם טריגר
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                      <MonetizationOnIcon color="primary" fontSize="large" />
                    </Box>
                    <Typography sx={styles.statValue} color={stats.withTriggerAverageR > 0 ? 'success.main' : 'error.main'}>
                      {roundNumber(stats.withTriggerAverageR)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={6} md={3}>
                <Card sx={styles.statsCard}>
                  <CardContent>
                    <Typography variant="subtitle1" align="center" gutterBottom>
                      משתמשים פעילים
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color="primary" fontSize="large" />
                    </Box>
                    <Typography sx={styles.statValue}>
                      {stats.totalUsers}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ניתוח עסקאות לפי טריגרים */}
        <Accordion sx={styles.accordion} expanded={expandedSection === 'triggerStats'} onChange={() => setExpandedSection(expandedSection === 'triggerStats' ? null : 'triggerStats')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
            <Box display="flex" alignItems="center">
              <FilterListIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">ביצועי עסקאות עם טריגר</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle1" sx={{ mb: 2 }} color="primary.main" fontWeight="bold">
              סטטיסטיקות עסקאות עם טריגר
            </Typography>
            
            {/* כמות עסקאות עם/בלי טריגר */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2, height: '100%', 
                            background: 'linear-gradient(to right, rgba(106, 17, 203, 0.05), rgba(37, 117, 252, 0.05))' }}>
                  <Typography variant="h6" align="center" gutterBottom color="primary.dark" fontWeight="bold">
                    כמות עסקאות
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" fontWeight="bold">עם טריגר</Typography>
                      <Typography variant="h4" color="primary.main" fontWeight="bold">
                        {stats.tradesWithTrigger}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {roundNumber((stats.tradesWithTrigger / stats.totalTrades) * 100)}% מסך העסקאות
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" fontWeight="bold">ללא טריגר</Typography>
                      <Typography variant="h4" color="text.secondary" fontWeight="bold">
                        {stats.tradesWithoutTrigger}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {roundNumber((stats.tradesWithoutTrigger / stats.totalTrades) * 100)}% מסך העסקאות
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              
              {/* ביצועים של עסקאות עם טריגר */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2, height: '100%', 
                            background: 'linear-gradient(to right, rgba(106, 17, 203, 0.05), rgba(37, 117, 252, 0.05))' }}>
                  <Typography variant="h6" align="center" gutterBottom color="primary.dark" fontWeight="bold">
                    ביצועי עסקאות עם טריגר
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" fontWeight="bold">Win Rate</Typography>
                      <Typography variant="h4" 
                        color={stats.triggerSuccessRate > 60 ? "success.main" : 
                               stats.triggerSuccessRate > 40 ? "primary.main" : "warning.main"} 
                        fontWeight="bold">
                        {roundNumber(stats.triggerSuccessRate)}%
                      </Typography>
                      <Chip 
                        label={stats.triggerSuccessRate > 60 ? "מצוין" : stats.triggerSuccessRate > 40 ? "טוב" : "דורש שיפור"} 
                        color={stats.triggerSuccessRate > 60 ? "success" : stats.triggerSuccessRate > 40 ? "primary" : "warning"}
                        size="small" 
                        sx={{ mt: 1 }}
                      />
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" fontWeight="bold">ממוצע R</Typography>
                      <Typography variant="h4" 
                        color={stats.withTriggerAverageR > 0.5 ? "success.main" : 
                               stats.withTriggerAverageR > 0 ? "primary.main" : "error.main"} 
                        fontWeight="bold">
                        {roundNumber(stats.withTriggerAverageR)}
                      </Typography>
                      <Chip 
                        label={stats.withTriggerAverageR > 0.5 ? "מצוין" : stats.withTriggerAverageR > 0 ? "חיובי" : "שלילי"} 
                        color={stats.withTriggerAverageR > 0.5 ? "success" : stats.withTriggerAverageR > 0 ? "primary" : "error"}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
            
            {/* סטטיסטיקות מפורטות של עסקאות עם טריגר */}
            <Typography variant="subtitle1" sx={{ mb: 2, mt: 3 }} color="primary.main" fontWeight="bold">
              ניתוח עסקאות עם טריגר לפי סשן
            </Typography>
            
            <TableContainer component={Paper} sx={{ mb: 3, boxShadow: 2, borderRadius: 2 }}>
              <Table>
                <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                  <TableRow>
                    <TableCell align="right">סשן</TableCell>
                    <TableCell align="center">כמות עסקאות</TableCell>
                    <TableCell align="center">Win Rate</TableCell>
                    <TableCell align="center">ממוצע R</TableCell>
                    <TableCell align="center">ביצועים</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // סטטיסטיקות עסקאות עם טריגר לפי סשן
                    const tradesWithTrigger = trades.filter(t => t.triggerAppeared === 'כן');
                    
                    // חישוב לפי סשן
                    const sessionData = {};
                    tradesWithTrigger.forEach(trade => {
                      const session = trade.session || 'לא צוין';
                      if (!sessionData[session]) {
                        sessionData[session] = { count: 0, wins: 0, r: 0 };
                      }
                      sessionData[session].count++;
                      if (trade.outcome === 'Take Profit') {
                        sessionData[session].wins++;
                      }
                      sessionData[session].r += getTradeR(trade);
                    });
                    
                    // מיון הסשנים לפי כמות עסקאות
                    const sortedSessions = Object.entries(sessionData)
                      .map(([name, data]) => ({
                        name,
                        count: data.count,
                        winRate: data.count ? (data.wins / data.count) * 100 : 0,
                        avgR: data.count ? data.r / data.count : 0
                      }))
                      .sort((a, b) => b.count - a.count);
                    
                    if (sortedSessions.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            אין עסקאות עם טריגר
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return sortedSessions.map((session) => (
                      <TableRow key={session.name} 
                        sx={{ 
                          bgcolor: session.avgR > 0.3 ? alpha(theme.palette.success.main, 0.05) : 
                                  session.avgR < 0 ? alpha(theme.palette.error.main, 0.05) : 'inherit'
                        }}
                      >
                        <TableCell align="right">{session.name}</TableCell>
                        <TableCell align="center">{session.count}</TableCell>
                        <TableCell align="center">{roundNumber(session.winRate)}%</TableCell>
                        <TableCell align="center">{roundNumber(session.avgR)}</TableCell>
                        <TableCell align="center">
                          {session.avgR > 0.5 ? (
                            <Chip label="מצוין" color="success" size="small" />
                          ) : session.avgR > 0 ? (
                            <Chip label="חיובי" color="primary" size="small" />
                          ) : (
                            <Chip label="שלילי" color="error" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* סטטיסטיקות לפי סוגי טריגרים */}
            <Typography variant="subtitle1" sx={{ mb: 2 }} color="primary.main" fontWeight="bold">
              ביצועים לפי סוגי טריגרים
            </Typography>
            
            <TableContainer component={Paper} sx={{ mb: 3, boxShadow: 2, borderRadius: 2 }}>
              <Table>
                <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                  <TableRow>
                    <TableCell align="right">סוג טריגר</TableCell>
                    <TableCell align="center">כמות עסקאות</TableCell>
                    <TableCell align="center">Win Rate</TableCell>
                    <TableCell align="center">ממוצע R</TableCell>
                    <TableCell align="center">ביצועים</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // סטטיסטיקות לפי סוגי טריגרים
                    const tradesWithTrigger = trades.filter(t => t.triggerAppeared === 'כן');
                    
                    // חישוב לפי סוג טריגר
                    const triggerTypeData = {};
                    tradesWithTrigger.forEach(trade => {
                      const triggerType = trade.matchedTrigger || 'לא צוין';
                      if (!triggerTypeData[triggerType]) {
                        triggerTypeData[triggerType] = { count: 0, wins: 0, r: 0 };
                      }
                      triggerTypeData[triggerType].count++;
                      if (trade.outcome === 'Take Profit') {
                        triggerTypeData[triggerType].wins++;
                      }
                      triggerTypeData[triggerType].r += getTradeR(trade);
                    });
                    
                    // מיון הטריגרים לפי ביצועים
                    const sortedTriggers = Object.entries(triggerTypeData)
                      .map(([name, data]) => ({
                        name,
                        count: data.count,
                        winRate: data.count ? (data.wins / data.count) * 100 : 0,
                        avgR: data.count ? data.r / data.count : 0
                      }))
                      .sort((a, b) => b.count - a.count);
                    
                    if (sortedTriggers.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            אין נתונים על סוגי טריגרים
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return sortedTriggers.map((trigger) => (
                      <TableRow key={trigger.name} 
                        sx={{ 
                          bgcolor: trigger.avgR > 0.3 ? alpha(theme.palette.success.main, 0.05) : 
                                  trigger.avgR < 0 ? alpha(theme.palette.error.main, 0.05) : 'inherit'
                        }}
                      >
                        <TableCell align="right">{trigger.name}</TableCell>
                        <TableCell align="center">{trigger.count}</TableCell>
                        <TableCell align="center">{roundNumber(trigger.winRate)}%</TableCell>
                        <TableCell align="center">{roundNumber(trigger.avgR)}</TableCell>
                        <TableCell align="center">
                          {trigger.avgR > 0.5 ? (
                            <Chip label="מצוין" color="success" size="small" />
                          ) : trigger.avgR > 0 ? (
                            <Chip label="חיובי" color="primary" size="small" />
                          ) : (
                            <Chip label="שלילי" color="error" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* סטטיסטיקות לפי זמן */}
        <Accordion sx={styles.accordion} expanded={expandedSection === 'timeStats'} onChange={() => setExpandedSection(expandedSection === 'timeStats' ? null : 'timeStats')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
            <Box display="flex" alignItems="center">
              <AccessTimeIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">ניתוח לפי זמן</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {/* סטטיסטיקות לפי שעות ביום */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    ביצועים לפי שעות היום
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>שעות</TableCell>
                          <TableCell align="center">עסקאות</TableCell>
                          <TableCell align="center">Win Rate</TableCell>
                          <TableCell align="center">ממוצע R</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(stats.timeOfDayStats).map(([timeRange, data]) => (
                          <TableRow key={timeRange}>
                            <TableCell>{timeRange}</TableCell>
                            <TableCell align="center">{data.count}</TableCell>
                            <TableCell align="center">
                              {data.count ? roundNumber((data.wins / data.count) * 100) : 0}%
                            </TableCell>
                            <TableCell align="center">
                              {data.count ? roundNumber(data.r / data.count) : 0}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
              
              {/* סטטיסטיקות לפי ימות השבוע */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    ביצועים לפי ימות השבוע
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>יום</TableCell>
                          <TableCell align="center">עסקאות</TableCell>
                          <TableCell align="center">Win Rate</TableCell>
                          <TableCell align="center">ממוצע R</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(stats.dayOfWeekStats)
                          .sort((a, b) => {
                            const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת', 'לא צוין'];
                            return days.indexOf(a[0]) - days.indexOf(b[0]);
                          })
                          .map(([day, data]) => (
                            <TableRow key={day}>
                              <TableCell>{day}</TableCell>
                              <TableCell align="center">{data.count}</TableCell>
                              <TableCell align="center">
                                {data.count ? roundNumber((data.wins / data.count) * 100) : 0}%
                              </TableCell>
                              <TableCell align="center">
                                {data.count ? roundNumber(data.r / data.count) : 0}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* רשימת משתמשים - קלאדיוס צד ימין */}
        <Card sx={{ mt: 3, boxShadow: 3, p: 2, borderRadius: '12px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              <PersonIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
              רשימת משתמשים
            </Typography>
            
            <TextField
              placeholder="חיפוש משתמש..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
              sx={styles.searchInput}
            />
          </Box>
          
          <List>
            {getFilteredUsers().map((user) => (
              <Paper
                key={user.uid}
                elevation={1}
                sx={styles.userListItem}
                onClick={() => handleUserClick(user)}
              >
                <ListItem>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                    {user.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <ListItemText 
                    primary={
                      <Typography variant="subtitle1" fontWeight="medium">
                        {user.name}
                        {user.tradesCount > 0 && (
                          <Chip 
                            size="small" 
                            label={`${user.tradesCount} עסקאות`} 
                            sx={{ ml: 1 }} 
                            color="primary" 
                            variant="outlined"
                          />
                        )}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" component="span">
                          Win Rate: {roundNumber(user.winRate)}% | ממוצע R: {roundNumber(user.averageR)}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box>
                    <IconButton color="primary" size="small">
                      <VisibilityIcon />
                    </IconButton>
                  </Box>
                </ListItem>
                <Box sx={{ px: 2, pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={user.progress} 
                        color={user.averageR > 0.2 ? "success" : user.averageR > 0 ? "primary" : "error"}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">
                        {`${roundNumber(user.progress)}%`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}
          </List>
        </Card>

        {/* דיאלוג יצירת משתמש חדש */}
        <Dialog 
          open={openCreateUserDialog} 
          onClose={() => setOpenCreateUserDialog(false)} 
          fullWidth 
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            },
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              יצירת משתמש חדש
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="שם משתמש"
                  variant="outlined"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  sx={{ mb: 2, mt: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="סיסמה"
                  type="password"
                  variant="outlined"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <InputLabel>טריגר</InputLabel>
                  <Select
                    value={newUserTrigger}
                    onChange={(e) => setNewUserTrigger(e.target.value)}
                    label="טריגר"
                  >
                    <MenuItem value="BUTTC/RBTI">BUTTC/RBTI</MenuItem>
                    <MenuItem value="טיסה/שבירת כנף">טיסה/שבירת כנף</MenuItem>
                    <MenuItem value="UT/SP">UT/SP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="שעות מסחר"
                  variant="outlined"
                  value={newUserTradingHours}
                  onChange={(e) => setNewUserTradingHours(e.target.value)}
                  placeholder="לדוגמה: 12:00-18:30 (ניו יורק)"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setOpenCreateUserDialog(false)}
              variant="outlined"
              sx={styles.actionButton}
            >
              ביטול
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleCreateUser}
              sx={styles.actionButton}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'צור משתמש'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* דיאלוג פרטי משתמש - משופר */}
        <Dialog 
          open={Boolean(selectedUser)} 
          onClose={handleCloseUserDialog} 
          fullWidth 
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              maxHeight: '90vh',
            },
          }}
        >
          {selectedUser && (
            <>
              <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      {selectedUser.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="h6">
                      עסקאות של {selectedUser.name}
                    </Typography>
                  </Box>
                  <IconButton onClick={handleCloseUserDialog}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                <Tabs 
                  value={userTabValue} 
                  onChange={(_, newValue) => setUserTabValue(newValue)}
                  sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                  centered
                >
                  <Tab label="סטטיסטיקות" icon={<EqualizerIcon />} iconPosition="start" />
                  <Tab label="עסקאות" icon={<ShowChartIcon />} iconPosition="start" />
                </Tabs>
                
                {/* טאב סטטיסטיקות */}
                {userTabValue === 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom align="center">
                          סיכום ביצועים
                        </Typography>
                        
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="body1">סה"כ עסקאות:</Typography>
                            <Typography variant="h6" fontWeight="bold">{userTrades.length}</Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="body1">ממוצע R:</Typography>
                            <Typography 
                              variant="h6" 
                              fontWeight="bold"
                              color={selectedUser.averageR > 0 ? 'success.main' : 'error.main'}
                            >
                              {roundNumber(selectedUser.averageR)} R
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="body1">WIN RATE:</Typography>
                            <Typography 
                              variant="h6" 
                              fontWeight="bold"
                              color={selectedUser.winRate > 50 ? 'success.main' : 'warning.main'}
                            >
                              {roundNumber(selectedUser.winRate)}%
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="body1">עסקאות מוצלחות:</Typography>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                              {selectedUser.wins}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="body1">עסקאות מפסידות:</Typography>
                            <Typography variant="h6" fontWeight="bold" color="error.main">
                              {selectedUser.losses}
                            </Typography>
                          </Box>
                        </Stack>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={8}>
                      <Card sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom align="center">
                          ניתוח עסקאות
                        </Typography>
                        
                        <Grid container spacing={2}>
                          {/* סטטיסטיקות טריגרים */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                <FilterListIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                טריגרים
                              </Typography>
                              
                              {(() => {
                                const withTrigger = userTrades.filter(t => t.triggerAppeared === 'כן').length;
                                const withoutTrigger = userTrades.filter(t => t.triggerAppeared !== 'כן').length;
                                const successWithTrigger = userTrades.filter(t => t.triggerAppeared === 'כן' && t.outcome === 'Take Profit').length;
                                const triggerSuccessRate = withTrigger ? (successWithTrigger / withTrigger) * 100 : 0;
                                
                                return (
                                  <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography variant="body2">עסקאות עם טריגר:</Typography>
                                      <Typography variant="body2" fontWeight="bold">{withTrigger}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography variant="body2">עסקאות ללא טריגר:</Typography>
                                      <Typography variant="body2" fontWeight="bold">{withoutTrigger}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography variant="body2">הצלחה עם טריגר:</Typography>
                                      <Typography variant="body2" fontWeight="bold">{roundNumber(triggerSuccessRate)}%</Typography>
                                    </Box>
                                  </Stack>
                                );
                              })()}
                            </Box>
                          </Grid>
                          
                          {/* סטטיסטיקות זמנים */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                <AccessTimeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                שעות/ימים מועדפים
                              </Typography>
                              
                              {(() => {
                                // חישוב יום מועדף
                                const dayCount = {};
                                const daySuccess = {};
                                
                                userTrades.forEach(trade => {
                                  const day = trade.dayOfWeek || 'לא צוין';
                                  dayCount[day] = (dayCount[day] || 0) + 1;
                                  if (trade.outcome === 'Take Profit') {
                                    daySuccess[day] = (daySuccess[day] || 0) + 1;
                                  }
                                });
                                
                                let bestDay = 'אין מספיק נתונים';
                                let bestDayRate = 0;
                                
                                for (const day in dayCount) {
                                  if (dayCount[day] >= 3) { // מינימום 3 עסקאות ליום
                                    const successRate = (daySuccess[day] || 0) / dayCount[day];
                                    if (successRate > bestDayRate) {
                                      bestDayRate = successRate;
                                      bestDay = day;
                                    }
                                  }
                                }
                                
                                // חישוב שעה מועדפת
                                const hourCount = {};
                                const hourSuccess = {};
                                
                                userTrades.forEach(trade => {
                                  if (trade.time) {
                                    const hour = trade.time.split(':')[0];
                                    hourCount[hour] = (hourCount[hour] || 0) + 1;
                                    if (trade.outcome === 'Take Profit') {
                                      hourSuccess[hour] = (hourSuccess[hour] || 0) + 1;
                                    }
                                  }
                                });
                                
                                let bestHour = 'אין מספיק נתונים';
                                let bestHourRate = 0;
                                
                                for (const hour in hourCount) {
                                  if (hourCount[hour] >= 3) { // מינימום 3 עסקאות לשעה
                                    const successRate = (hourSuccess[hour] || 0) / hourCount[hour];
                                    if (successRate > bestHourRate) {
                                      bestHourRate = successRate;
                                      bestHour = hour;
                                    }
                                  }
                                }
                                
                                return (
                                  <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography variant="body2">יום מועדף:</Typography>
                                      <Typography variant="body2" fontWeight="bold">
                                        {bestDay} {bestDay !== 'אין מספיק נתונים' && `(${roundNumber(bestDayRate * 100)}%)`}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography variant="body2">שעה מועדפת:</Typography>
                                      <Typography variant="body2" fontWeight="bold">
                                        {bestHour} {bestHour !== 'אין מספיק נתונים' && `(${roundNumber(bestHourRate * 100)}%)`}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                );
                              })()}
                            </Box>
                          </Grid>
                          
                          {/* משך עסקאות */}
                          <Grid item xs={12}>
                            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                <EventIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                משך עסקאות
                              </Typography>
                              
                              {(() => {
                                // חישוב זמן ממוצע לעסקה
                                const validDurations = userTrades
                                  .filter(trade => trade.duration && !isNaN(parseInt(trade.duration)))
                                  .map(trade => parseInt(trade.duration));
                                
                                const avgDuration = validDurations.length > 0
                                  ? validDurations.reduce((sum, duration) => sum + duration, 0) / validDurations.length
                                  : 0;
                                
                                // חישוב משך ממוצע לעסקה מוצלחת/מפסידה
                                const successDurations = userTrades
                                  .filter(trade => trade.outcome === 'Take Profit' && trade.duration && !isNaN(parseInt(trade.duration)))
                                  .map(trade => parseInt(trade.duration));
                                
                                const failDurations = userTrades
                                  .filter(trade => trade.outcome === 'Stop Loss' && trade.duration && !isNaN(parseInt(trade.duration)))
                                  .map(trade => parseInt(trade.duration));
                                
                                const avgSuccessDuration = successDurations.length > 0
                                  ? successDurations.reduce((sum, duration) => sum + duration, 0) / successDurations.length
                                  : 0;
                                
                                const avgFailDuration = failDurations.length > 0
                                  ? failDurations.reduce((sum, duration) => sum + duration, 0) / failDurations.length
                                  : 0;
                                
                                return (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">זמן ממוצע לעסקה: {Math.round(avgDuration)} דקות</Typography>
                                    <Box>
                                      <Typography variant="body2" color="success.main">
                                        עסקאות מוצלחות: {Math.round(avgSuccessDuration)} דקות
                                      </Typography>
                                      <Typography variant="body2" color="error.main">
                                        עסקאות מפסידות: {Math.round(avgFailDuration)} דקות
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })()}
                            </Box>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  </Grid>
                )}
                
                {/* טאב עסקאות */}
                {userTabValue === 1 && (
                  <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {columns.map((col) => (
                            <TableCell 
                              key={col.id} 
                              align={col.align || 'right'}
                              sx={{ 
                                fontWeight: 'bold',
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                              }}
                            >
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userTrades.map((trade) => (
                          <TableRow 
                            key={trade.id} 
                            sx={styles.tradeRow(trade.outcome)}
                          >
                            <TableCell align="right">{trade.date}</TableCell>
                            <TableCell align="right">{trade.dayOfWeek}</TableCell>
                            <TableCell align="right">{trade.time}</TableCell>
                            <TableCell align="right">{trade.session}</TableCell>
                            <TableCell align="center">{renderYesNoIcon(trade.triggerAppeared)}</TableCell>
                            <TableCell align="right">{trade.matchedTrigger}</TableCell>
                            <TableCell align="right">{trade.report}</TableCell>
                            <TableCell align="right">{trade.stopPercentage}</TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={trade.outcome} 
                                sx={styles.outcomeChip(trade.outcome)}
                                size="small"
                                icon={renderOutcomeIcon(trade.outcome)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {trade.tpMilestones
                                ? trade.tpMilestones
                                    .map((m, idx) => (
                                      <Chip 
                                        key={idx}
                                        size="small"
                                        label={m.tp} 
                                        color={m.status === 'הושג' ? 'success' : 'default'}
                                        variant={m.status === 'הושג' ? 'filled' : 'outlined'}
                                        sx={{ m: 0.5 }}
                                      />
                                    ))
                                : trade.tpSelected || '-'}
                            </TableCell>
                            <TableCell align="right">{trade.duration}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>
              <DialogActions>
                <Button 
                  onClick={handleCloseUserDialog}
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  sx={styles.actionButton}
                >
                  סגור
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </Box>
  );
};

export default AdminPanel;